/**
 * IELTS Speaking 分Part提示词配置
 * 用于口语评估的专用提示词模板
 */

export interface PromptConfig {
  part: 'Part 1' | 'Part 2' | 'Part 3';
  systemRole: string;
  instructionTemplate: string;
  outputFormat: string;
  maxOutputTokens: number;
}

/**
 * Part 1 提示词配置（Task23规范）
 * 特点：日常口语、简短自然、主动追问细节、短语积累
 */
export const PART1_PROMPT: PromptConfig = {
  part: 'Part 1',
  systemRole: 'You are an IELTS Speaking examiner for Part 1. Provide comprehensive feedback with phrase extraction.',
  instructionTemplate: `Question: "{question}"
Topic: "{topic}"

Instructions:
1. **Transcribe exactly** what the speaker said (rawTranscript)
2. **Understand meaning**: Summarize in Chinese what they really wanted to express (meaningSummary)
3. **Fluency issues summary**: Write 1-3 Chinese sentences describing the MAIN fluency problems that affected this answer:
   - Repetitions (重复单词或短语)
   - Structural confusion (结构混乱、句子断裂)
   - Self-corrections (说到一半改词改结构)
   - Confusing expressions (让听众困惑的表达)
   For each issue, specify WHERE it happened and WHY it affected fluency (stuckPoints)
4. **Rewrite answer**: Provide a clearer, more natural version in English without changing core meaning
   Aim for 2–4 sentences (around 35–60 English words) so it sounds like a concise but complete Part 1 answer, not a speech.
6. **Extract phrases**: Select up to 3 meaningful expressions that EXPRESS SPECIFIC meanings (NOT generic connectors like "in my opinion", "for example"):
   - phrase: 2–5 word chunk that carries concrete semantic meaning (e.g. "pretty mild weather", "can't imagine living without", "takes a toll on")
   - cnHint: 简短中文解释（20字以内）
   - whenToUse: 说明这个短语表达什么具体意思，在什么场景下用
   - exampleSentence: 一句自然的日常口语例句
   - IMPORTANT: Each phrase must describe a situation/feeling/action, NOT just link sentences
7. **Strict scoring**: Rate 0-9 on fluency, lexical resource, grammatical range, pronunciation

Focus on natural conversation. Encourage concrete details and specific examples.`,
  outputFormat: `Return JSON format (no markdown, pure JSON):
{
  "rawTranscript": "exact transcription",
  "meaningSummary": "中文概括用户想说的意思",
  "stuckPoints": ["流利度问题1：在哪里+为什么影响流利度", "流利度问题2"],
  "rewrittenAnswer": "improved English version",
  "phraseSuggestions": [
    {"phrase": "expression", "cnHint": "中文提示", "whenToUse": "使用场景", "exampleSentence": "example"}
  ],
  "strictScores": {"fluency": 0, "lexicalResource": 0, "grammaticalRange": 0, "pronunciation": 0}
}`,
  maxOutputTokens: 600,
};

/**
 * Part 2 提示词配置（Task23规范）
 * 特点：完整叙事框架、引导补充细节、提供角度选择、短语积累（最多6条）
 */
export const PART2_PROMPT: PromptConfig = {
  part: 'Part 2',
  systemRole: 'You are an IELTS Speaking examiner for Part 2. Provide comprehensive feedback with phrase extraction.',
  instructionTemplate: `Topic: "{topic}"
Cues: {cues}

Instructions:
1. **Transcribe exactly** what the speaker said (rawTranscript)
2. **Understand meaning**: Summarize in Chinese what they really wanted to express (meaningSummary)
3. **Fluency issues summary**: Write 1-3 Chinese sentences describing the MAIN fluency problems that affected this answer:
   - Repetitions (重复单词或短语)
   - Structural confusion (结构混乱、句子断裂)
   - Self-corrections (说到一半改词改结构)
   - Confusing expressions (让听众困惑的表达)
   For each issue, specify WHERE it happened and WHY it affected fluency (stuckPoints)
4. **Rewrite answer**: Provide a more structured version following Background → Event → Feelings
   Aim for roughly 130–200 English words (about 1.5–2 minutes of speaking), with several short sentences covering the cue points.
6. **Extract phrases**: Select up to 6 meaningful expressions that EXPRESS SPECIFIC meanings (NOT generic connectors like "first of all", "in conclusion"):
   - phrase: 2–5 word chunk that carries concrete semantic meaning (e.g. "got totally caught up in", "couldn't help but smile", "it really hit me that")
   - cnHint: 简短中文解释（20字以内）
   - whenToUse: 说明这个短语表达什么具体意思/感受
   - exampleSentence: 一句自然的日常口语例句
   - IMPORTANT: Each phrase must describe a situation/emotion/action, NOT just structural transitions
7. **Strict scoring**: Rate 0-9 on fluency, lexical resource, grammatical range, pronunciation

Encourage adding specific examples, timeframes, and emotional reflections.`,
  outputFormat: `Return JSON format (no markdown, pure JSON):
{
  "rawTranscript": "exact transcription",
  "meaningSummary": "中文概括用户想说的意思",
  "stuckPoints": ["流利度问题1：在哪里+为什么影响流利度", "流利度问题2"],
  "rewrittenAnswer": "improved English version with richer details",
  "phraseSuggestions": [
    {"phrase": "expression", "cnHint": "中文提示", "whenToUse": "使用场景", "exampleSentence": "example"}
  ],
  "strictScores": {"fluency": 0, "lexicalResource": 0, "grammaticalRange": 0, "pronunciation": 0}
}`,
  maxOutputTokens: 800,
};

/**
 * Part 3 提示词配置（Task23规范）
 * 特点：论点+分类+例证、强调举例、避免翻译、短语积累
 */
export const PART3_PROMPT: PromptConfig = {
  part: 'Part 3',
  systemRole: 'You are an IELTS Speaking examiner for Part 3. Provide comprehensive feedback with phrase extraction.',
  instructionTemplate: `Question: "{question}"
Topic: "{topic}"

Instructions:
1. **Transcribe exactly** what the speaker said (rawTranscript)
2. **Understand meaning**: Summarize in Chinese what they really wanted to express (meaningSummary)
3. **Fluency issues summary**: Write 1-3 Chinese sentences describing the MAIN fluency problems that affected this answer:
   - Repetitions (重复单词或短语)
   - Structural confusion (结构混乱、句子断裂)
   - Self-corrections (说到一半改词改结构)
   - Confusing expressions (让听众困惑的表达)
   For each issue, specify WHERE it happened and WHY it affected fluency (stuckPoints)
4. **Rewrite answer**: Provide a version with clear viewpoint + classification/comparison + concrete examples
   Aim for 3–5 sentences (around 60–90 English words) so it feels like a developed but still spoken-style Part 3 answer.
6. **Extract phrases**: Select up to 3 meaningful expressions that EXPRESS SPECIFIC meanings (NOT generic connectors like "on the other hand", "in conclusion"):
   - phrase: 2–5 word chunk that carries concrete semantic meaning (e.g. "plays a crucial role in", "has a significant impact on", "tend to overlook the fact that")
   - cnHint: 简短中文解释（20字以内）
   - whenToUse: 说明这个短语表达什么具体概念/关系
   - exampleSentence: 一句自然的口语句子
   - IMPORTANT: Each phrase must express a specific concept/relationship, NOT just link paragraphs
7. **Strict scoring**: Rate 0-9 on fluency, lexical resource, grammatical range, pronunciation

DO NOT just translate. Guide critical thinking with evidence and real-world examples.`,
  outputFormat: `Return JSON format (no markdown, pure JSON):
{
  "rawTranscript": "exact transcription",
  "meaningSummary": "中文概括用户想说的意思",
  "stuckPoints": ["流利度问题1：在哪里+为什么影响流利度", "流利度问题2"],
  "rewrittenAnswer": "improved version with opinion + reasoning + examples",
  "phraseSuggestions": [
    {"phrase": "expression", "cnHint": "中文提示", "whenToUse": "使用场景", "exampleSentence": "example"}
  ],
  "strictScores": {"fluency": 0, "lexicalResource": 0, "grammaticalRange": 0, "pronunciation": 0}
}`,
  maxOutputTokens: 700,
};

/**
 * 草稿生成提示词配置
 */
export interface DraftPromptConfig {
  part: 'Part 1' | 'Part 2' | 'Part 3';
  systemRole: string;
  instructionTemplate: string;
  outputFormat: string;
}

/**
 * Part 1 草稿生成提示词
 * 特点：日常口语、简短自然、2-4句、35-60词
 */
export const PART1_DRAFT_PROMPT: DraftPromptConfig = {
  part: 'Part 1',
  systemRole: 'You are an IELTS Speaking coach helping a student prepare for Part 1.',
  instructionTemplate: `Question: "{question}"
Topic: "{topic}"
{historyContext}
Student's draft idea: "{draft}"

Based on the student's draft, provide:
1. A complete example answer (natural spoken English, 2-4 sentences, 35-60 words)
2. 3 meaningful phrases that express SPECIFIC meanings (NOT generic connectors like "in my opinion", "for example")

Style guide:
- Write like you talk, not like an essay
- Phrases should express concrete meanings that take time to learn (e.g. "pretty mild weather", "can't imagine living without", "a bit stressful at times")
- Each phrase should describe a situation, feeling, or action - not just link sentences`,
  outputFormat: `Return in this EXACT format (no markdown):
EXAMPLE:
<complete example answer, 2-4 sentences>
PHRASES:
- <phrase 1: meaningful expression with specific meaning, NOT generic connector>
- <phrase 2: describes a concrete situation/feeling>
- <phrase 3>`,
};

/**
 * Part 2 草稿生成提示词
 * 特点：完整叙事框架、Background → Event → Feelings、130-200词
 */
export const PART2_DRAFT_PROMPT: DraftPromptConfig = {
  part: 'Part 2',
  systemRole: 'You are an IELTS Speaking coach helping a student prepare for Part 2.',
  instructionTemplate: `Topic: "{topic}"
Cues: {cues}
{historyContext}
Student's draft idea: "{draft}"

Based on the student's draft, provide:
1. A complete example answer following: Background → Event → Feelings (130-200 words, about 1.5-2 minutes)
2. Up to 6 meaningful phrases that EXPRESS SPECIFIC meanings (NOT generic connectors like "first of all", "in conclusion")

Style guide:
- Write like you talk, suitable for telling a story
- Phrases must describe concrete situations, emotions, or actions (e.g. "got totally caught up in", "couldn't help but smile", "it really hit me that")
- Each phrase should carry semantic meaning that takes time to accumulate - not just structural words
- Include specific examples, timeframes, and emotional reflections`,
  outputFormat: `Return in this EXACT format (no markdown):
EXAMPLE:
<complete example answer following Background → Event → Feelings, 130-200 words>
PHRASES:
- <phrase 1: describes a specific feeling/situation, NOT generic connector>
- <phrase 2: meaningful expression>
- <phrase 3>
- <phrase 4>
- <phrase 5>
- <phrase 6>`,
};

/**
 * Part 3 草稿生成提示词
 * 特点：论点+分类+例证、3-5句、60-90词
 */
export const PART3_DRAFT_PROMPT: DraftPromptConfig = {
  part: 'Part 3',
  systemRole: 'You are an IELTS Speaking coach helping a student prepare for Part 3.',
  instructionTemplate: `Question: "{question}"
Topic: "{topic}"
{historyContext}
Student's draft idea: "{draft}"

Based on the student's draft, provide:
1. A complete example answer with: clear viewpoint + classification/comparison + concrete examples (3-5 sentences, 60-90 words)
2. 3 meaningful phrases that EXPRESS SPECIFIC meanings (NOT generic connectors like "on the other hand", "in conclusion")

Style guide:
- Write like spoken English, not academic writing
- Phrases must carry concrete semantic meaning (e.g. "plays a crucial role in", "has a significant impact on", "tend to overlook the fact that")
- Each phrase should express a specific concept/relationship that takes time to learn - not just structural transitions
- Guide critical thinking with evidence and real-world examples
- DO NOT just translate the draft`,
  outputFormat: `Return in this EXACT format (no markdown):
EXAMPLE:
<complete example answer with viewpoint + reasoning + examples, 3-5 sentences>
PHRASES:
- <phrase 1: expresses a specific concept/relationship, NOT generic connector>
- <phrase 2: meaningful discussion expression>
- <phrase 3>`,
};

/**
 * 根据Part类型选择草稿生成提示词
 */
export function selectDraftPromptByPart(part?: string): DraftPromptConfig {
  const normalized = part?.toLowerCase().includes('part 2')
    ? 'Part 2'
    : part?.toLowerCase().includes('part 3')
      ? 'Part 3'
      : 'Part 1';
  
  switch (normalized) {
    case 'Part 2':
      return PART2_DRAFT_PROMPT;
    case 'Part 3':
      return PART3_DRAFT_PROMPT;
    default:
      return PART1_DRAFT_PROMPT;
  }
}

/**
 * 构建草稿生成提示词
 */
export function buildDraftPrompt(params: {
  part?: string;
  question?: string;
  topic?: string;
  cues?: string[];
  draft?: string;
  historyContext?: string;
  previousExampleAnswer?: string;
}): string {
  const config = selectDraftPromptByPart(params.part);

  let prompt = config.systemRole + '\n\n';

  let instruction = config.instructionTemplate
    .replace('{question}', params.question || 'N/A')
    .replace('{topic}', params.topic || 'N/A')
    .replace('{cues}', params.cues?.join(', ') || 'N/A')
    .replace('{draft}', params.draft || 'N/A')
    .replace('{historyContext}', params.historyContext || '');

  // 当有之前的示例回答时，添加修改指令
  if (params.previousExampleAnswer) {
    instruction += `

=== IMPORTANT: Modification Mode ===
The student has already received this example answer:
"""
${params.previousExampleAnswer}
"""

The student is now providing feedback or requesting changes.
Listen carefully to what they want to modify (e.g., "change X to Y", "make it shorter", "add more detail about...").
Your task is to UPDATE the previous answer based on their request, NOT create a completely new answer.
Keep the parts they didn't mention, only modify what they specifically asked to change.
IMPORTANT: Even in modification mode, you MUST still output both EXAMPLE and PHRASES sections in the exact required format.
=== End of modification instructions ===`;
  }

  prompt += instruction + '\n\n' + config.outputFormat;

  return prompt;
}

/**
 * 根据Part类型选择对应的提示词配置
 */
export function selectPromptByPart(part?: string): PromptConfig {
  const normalized = part?.toLowerCase().includes('part 2')
    ? 'Part 2'
    : part?.toLowerCase().includes('part 3')
      ? 'Part 3'
      : 'Part 1';
  
  switch (normalized) {
    case 'Part 2':
      return PART2_PROMPT;
    case 'Part 3':
      return PART3_PROMPT;
    default:
      return PART1_PROMPT;
  }
}

/**
 * 构建完整的评估提示词
 */
export function buildEvaluationPrompt(params: {
  part?: string;
  question?: string;
  topic?: string;
  cues?: string[];
  draft?: string;
  historyContext?: string;
}): string {
  const config = selectPromptByPart(params.part);
  
  let prompt = config.systemRole + '\n\n';
  
  // 填充模板
  let instruction = config.instructionTemplate
    .replace('{question}', params.question || 'N/A')
    .replace('{topic}', params.topic || 'N/A')
    .replace('{cues}', params.cues?.join(', ') || 'N/A')
    .replace('{draft}', params.draft || 'N/A');
  
  // 添加历史上下文
  if (params.historyContext) {
    instruction += '\n\n' + params.historyContext;
  }
  
  prompt += instruction + '\n\n' + config.outputFormat;
  
  return prompt;
}
